import React, { useMemo } from 'react';

import {
  Typography,
  Datatable,
  DatatableBody,
  DatatableHeader,
  DatatableCell,
  DatatableRow,
} from '@qtm/react2';
import { useTheme } from '../ThemeContext';
import useMediaQuery from '../hooks/useMediaQuery';
import BackButton from '../components/BackButton';

const presentations = [
  {
    name: 'TDP presentation',
    description: 'Overview of TrustNest Platform services (compute, software solutions, subscriptions...)',
    duration: '1',
  },
  {
    name: 'TDP pricelist',
    description: 'Detailed presentation about TrustNest Platform service catalogue and costs',
    duration: '1',
  },
];



const professionalServicesItems = [
  'Tailored TrustNest Platform walkthroughs',
  'Architecture design and implementation guidance',
  'Cost estimation and optimisation workshops',
  'Hands-on onboarding sessions',
];

const heroLinks = [
  {
    label: 'TrustNest community',
    href: 'https://engage.cloud.microsoft/main/org/thalesgroup.onmicrosoft.com/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxODgyNzY4NTA2ODgifQ',
    external: true,
  },
  {
    label: 'Contact Digital Partners',
    href: 'https://thalesgroup.sharepoint.com/sites/TrustNest-Portal/SitePages/Digital%20Partners.aspx',
    external: true,
  },
];

const GettingStartedPage = (showBackButton) => {
  const { darkMode } = useTheme();
  const isTablet = useMediaQuery('(max-width: 1023px)');
  const isMobile = useMediaQuery('(max-width: 640px)');
  const pageStyle = useMemo(
    () => ({
      minHeight: '100vh',
      backgroundColor: darkMode ? '#0f172a' : '#f7f8fb',
      padding: isMobile ? '32px 16px 56px' : isTablet ? '48px 24px 72px' : '1px 2px 6px',
      color: darkMode ? '#e2e8f0' : '#0f172a',
    }),
    [darkMode, isMobile, isTablet],
  );

  const containerStyle = useMemo(
    () => ({
      maxWidth: 1100,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 32 : 48,
    }),
    [isMobile],
  );

  const heroStyles = useMemo(
    () => ({
      wrapper: {
        borderRadius: 36,
        padding: isMobile ? '32px 24px 40px' : isTablet ? '40px 32px 48px' : '48px 56px 56px',
        background: darkMode
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 35%, #1f2937 100%)'
          : 'linear-gradient(135deg, #729bcd 0%, #25286b 35%, #729bcd 100%)',
        color: '#fff',
        boxShadow: '0 24px 60px rgba(69, 38, 144, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 24 : 32,
        alignItems: isTablet ? 'flex-start' : 'center',
        textAlign: isTablet ? 'left' : 'center',
      },
      heading: {
        fontSize: isMobile ? '34px' : isTablet ? '46px' : '52px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        margin: 0,
      },
      subtitle: {
        fontSize: isMobile ? '16px' : '18px',
        margin: 0,
        maxWidth: isTablet ? '100%' : '720px',
        opacity: 0.92,
      },
      linksRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        justifyContent: isTablet ? 'flex-start' : 'center',
      },
      linkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 18px',
        borderRadius: 999,
        color: '#fff',
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.25)',
        fontWeight: 600,
        textDecoration: 'none',
      },
      image: {
        width: '100%',
        borderRadius: 24,
        boxShadow: darkMode
          ? '0 24px 40px rgba(15, 23, 42, 0.45)'
          : '0 24px 40px rgba(15, 23, 42, 0.25)',
      },
    }),
    [darkMode, isMobile, isTablet],
  );

  const contentCardStyle = useMemo(
    () => ({
      backgroundColor: darkMode ? '#111827' : '#ffffff',
      borderRadius: 24,
      padding: isMobile ? 24 : 32,
      boxShadow: darkMode
        ? '0 16px 32px rgba(15, 23, 42, 0.5)'
        : '0 16px 32px rgba(15, 23, 42, 0.08)',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 20 : 24,
    }),
    [darkMode, isMobile],
  );

  const stepsListStyle = useMemo(
    () => ({
      margin: 0,
      paddingLeft: isMobile ? '1.2rem' : '1.6rem',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 16 : 20,
    }),
    [isMobile],
  );

  const psListStyle = useMemo(
    () => ({
      margin: '8px 0 0',
      paddingLeft: isMobile ? '1.2rem' : '1.6rem',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }),
    [isMobile],
  );

  return (
    <div style={pageStyle}>
      {showBackButton && <BackButton />}
      <div style={containerStyle}>
        
        <section style={heroStyles.wrapper}>
          <div>
            <h1 style={heroStyles.heading}>Begin your Cloud journey on TrustNest</h1>
            <p style={heroStyles.subtitle}>
              Explore resources that help you discover TrustNest platforms, request expert support, subscribe to
              services, and level up your skills.
            </p>

          </div>

          <img src="images/trustnest.png" alt="TrustNest platforms" style={heroStyles.image} />
          <div style={heroStyles.linksRow}>
            {heroLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? '_blank' : '_self'}
                rel={link.external ? 'noopener noreferrer' : undefined}
                style={heroStyles.linkButton}
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>

        <section style={contentCardStyle}>
          <div>
            <Typography component="title-3">Your onboarding checklist</Typography>
            <Typography component="body-1">
              Follow these key steps to request access, discover platform capabilities, and get hands-on support from the
              TrustNest teams.
            </Typography>
          </div>

          <ol style={stepsListStyle}>
            <li>
              <Typography component="body-1">
                🔎 Discover the TrustNest platforms and their core services on our{' '}
                <a href="https://thalesgroup.sharepoint.com/sites/TrustNest-Portal">SharePoint</a>.
              </Typography>
            </li>
            <li>
              <Typography component="body-1">
                ✉️ Contact the Digital Partners with this{' '}
                <a
                  href="https://thalesgroup.sharepoint.com/sites/TrustNest-Portal/SitePages/Digital%20Partners.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  request form
                </a>{' '}
                to book a presentation. Popular briefings include:
              </Typography>
              <div style={{ marginTop: 12 }}>
                <Datatable size="medium" marginDensity="medium" arrayStyle="lines">
                  <DatatableHeader>
                    <DatatableRow>
                      <DatatableCell>Name</DatatableCell>
                      <DatatableCell>Description</DatatableCell>
                      <DatatableCell>Duration (hour)</DatatableCell>
                    </DatatableRow>
                  </DatatableHeader>
                  <DatatableBody>
                    {presentations.map((item) => (
                      <DatatableRow key={item.name}>
                        <DatatableCell>{item.name}</DatatableCell>
                        <DatatableCell>{item.description}</DatatableCell>
                        <DatatableCell>{item.duration}</DatatableCell>
                      </DatatableRow>
                    ))}
                  </DatatableBody>
                </Datatable>
              </div>
            </li>
            <li>
              <Typography component="body-1">
                🌐 Join the TrustNest{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://engage.cloud.microsoft/main/org/thalesgroup.onmicrosoft.com/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxODgyNzY4NTA2ODgifQ"
                >
                  community
                </a>{' '}
                to connect with peers and share insights.
              </Typography>
            </li>
            <li>
              <Typography component="body-1">
                🤝 Request{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://thalesgroup.sharepoint.com/sites/TrustNest-Portal/SitePages/TrustNest-Professional-Services-Catalog.aspx"
                >
                  Professional Services
                </a>{' '}
                for tailored guidance throughout your move-to-cloud journey. Support can cover:
              </Typography>
              <ul style={psListStyle}>
                {professionalServicesItems.map((item) => (
                  <li key={item}>
                    <Typography component="body-2">{item}</Typography>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Typography component="body-1">
                🫵 Subscribe to TrustNest services via{' '}
                <a href="https://postit.service-now.com/esc?id=tdf_ec_home">Postit</a> when you are ready to deploy.
              </Typography>
            </li>
            <li>
              <Typography component="body-1">
                🚀 Browse our{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://trustnest-docs.thalesdigital.io/docs/"
                >
                  documentation
                </a>{' '}
                to implement and operate your workloads.
              </Typography>
            </li>
            <li>
              <Typography component="body-1">
                🎓 Explore the{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://docs.trustnest.thalesdigital.io/learning/"
                >
                  learning section
                </a>{' '}
                to deepen your skills on the technologies powering TrustNest.
              </Typography>
            </li>
          </ol>
        </section>      </div>
    </div>
  );
};

export default GettingStartedPage;
